import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Link } from 'react-router-dom';
import { playLobbyButtonClickSound, playStartPageWelcomeSound } from '../utils/sounds';
import MuteToggle from './MuteToggle';
import ThemeToggleIcon from './ThemeToggleIcon';
import TutorialButton from './TutorialButton';
import { RetroLinkButton } from '../styles/RetroButton';
import { lightTheme, darkTheme, createSharedComponents, createFloatingLetterEffect } from '../styles/theme';
import Tutorial from './Tutorial';
import { analyticsEvents } from '../utils/analytics';

import { letterFloat, letterSwapLeft, letterSwapRight, letterGlitch } from '../styles/animations';

// Create shared components
const { SharedGlobalStyle, SharedBackgroundContainer } = createSharedComponents(styled, createGlobalStyle);

const PageContainer = styled.div`
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TopBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 1rem;
  box-sizing: border-box;
  
  @media (max-width: 500px) {
    padding: 0.5rem 1rem;
  }
`;

const CenteredContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem 1rem;
`;

const Title = styled.h1`
  font-family: 'Fredoka One', 'Bungee', cursive;
  font-size: 3rem;
  margin: 1rem 0;
  color: ${props => props.theme === 'light' ? '#F5EFE6' : '#EDE7D9'};
  text-shadow: 
    3px 3px 0px ${props => props.theme === 'light' ? '#E57A44' : '#D9822E'},
    6px 6px 0px ${props => props.theme === 'light' ? '#8A9A5B' : '#4B8E8D'},
    9px 9px 0px ${props => props.theme === 'light' ? '#8B4513' : '#3D2B1F'},
    12px 12px 25px rgba(0,0,0,0.4);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0;
  letter-spacing: 0.02em;
  filter: drop-shadow(0 0 8px rgba(0,0,0,0.2));

  @media (max-width: 500px) {
    font-size: 4rem;
    margin: 0.5rem 0;
    text-shadow: 
      2px 2px 0px ${props => props.theme === 'light' ? '#E57A44' : '#D9822E'},
      4px 4px 0px ${props => props.theme === 'light' ? '#8A9A5B' : '#4B8E8D'},
      6px 6px 0px ${props => props.theme === 'light' ? '#8B4513' : '#3D2B1F'},
      8px 8px 20px rgba(0,0,0,0.4);
  }
`;

const animationMap = {
  letterFloat,
  letterSwapLeft,
  letterSwapRight,
  letterGlitch,
};

const AnimatedLetter = styled.span`
  display: inline-block;
  animation: ${props => animationMap[props.$animationName]} ${props => props.$duration}s infinite;
  animation-delay: ${props => props.$delay}s;
  animation-fill-mode: both;
  will-change: transform;
  
  /* Pause animations when page is hidden for better performance */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// const Subtitle = styled.div`
//   font-size: 0.9rem;
//   margin: 1.5rem 0 2rem 0;
//   // font-weight: 700;
//   opacity: 0.9;
//   background: ${props => props.theme === 'light' 
//     ? 'rgba(255, 255, 255, 0.2)' 
//     : 'rgba(255, 255, 255, 0.15)'};
//   padding: 15px 25px;
//   border-radius: 20px;
//   backdrop-filter: blur(10px);
//   -webkit-backdrop-filter: blur(10px);
//   border: 1px solid ${props => props.theme === 'light' 
//     ? 'rgba(255, 255, 255, 0.3)' 
//     : 'rgba(255, 255, 255, 0.2)'};
//   box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
//   color: ${props => props.theme === 'light' ? '#2D2D2D' : '#F5F5F5'};
  
//   @media (max-width: 480px) {
//     font-size: 0.9rem;
//     padding: 10px 20px;
//     margin: 2rem 0 1.5rem 0;
//   }
// `;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: 300px;
  }
`;

const SEOSection = styled.section`
  width: 100%;
  max-width: 880px;
  margin: 2rem auto 0;
  padding: 1.5rem;
  border-radius: 1.5rem;
  background: ${props => props.$themeMode === 'light'
    ? 'rgba(255, 255, 255, 0.35)'
    : 'rgba(0, 0, 0, 0.25)'};
  color: ${props => props.$themeMode === 'light' ? '#2D2D2D' : '#F5F5F5'};
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  backdrop-filter: blur(12px);
  text-align: left;
`;

const SEOHeading = styled.h2`
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
  font-family: 'Nunito', sans-serif;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
`;

const SEOParagraph = styled.p`
  font-size: 0.82rem;
  line-height: 1.45;
  margin-bottom: 0.65rem;
  font-family: 'Nunito', sans-serif;
  font-weight: 400;
`;

const KeywordHighlight = styled.span`
  font-weight: 700;
`;

const Footer = styled.footer`
  width: 100%;
  padding: 1rem 0 0.5rem;
  text-align: center;
  font-size: 0.8rem;
  color: ${props => props.$themeMode === 'light' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.75)'};
  transition: color 0.3s ease;
`;

const FooterLink = styled.a`
  color: inherit;
  text-decoration: none;
  font-weight: 600;
  margin: 0 0.5rem;

  &:hover {
    text-decoration: underline;
  }
`;

const StartPage = () => {
  const [theme, setTheme] = useState('light'); // Re-add theme state
  const [hasPlayedWelcome, setHasPlayedWelcome] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // Use shared floating letter effect
    const cleanup = createFloatingLetterEffect(3000, 8, 1500);
    
    // Play welcome sound on first user interaction
    const playWelcomeOnInteraction = () => {
      if (!hasPlayedWelcome) {
        setTimeout(() => {
          playStartPageWelcomeSound();
        }, 100);
        setHasPlayedWelcome(true);
        // Remove listeners after first interaction
        document.removeEventListener('click', playWelcomeOnInteraction);
        document.removeEventListener('touchstart', playWelcomeOnInteraction);
        document.removeEventListener('keydown', playWelcomeOnInteraction);
      }
    };
    
    document.addEventListener('click', playWelcomeOnInteraction);
    document.addEventListener('touchstart', playWelcomeOnInteraction);
    document.addEventListener('keydown', playWelcomeOnInteraction);
    
    // Pause animations when page is hidden for better performance
    const handleVisibilityChange = () => {
      const letters = document.querySelectorAll('[data-animated-letter]');
      letters.forEach(letter => {
        letter.style.animationPlayState = document.hidden ? 'paused' : 'running';
      });
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      cleanup();
      document.removeEventListener('click', playWelcomeOnInteraction);
      document.removeEventListener('touchstart', playWelcomeOnInteraction);
      document.removeEventListener('keydown', playWelcomeOnInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasPlayedWelcome]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    // Save theme preference to localStorage
    localStorage.setItem('gameTheme', newTheme);
  };

  // Load theme preference from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('gameTheme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setTheme(savedTheme);
    }

    // Check if user has seen tutorial before
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    }
  }, []);

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    localStorage.setItem('hasSeenTutorial', 'true');
  };

  const handleShowTutorial = () => {
    setShowTutorial(true);
    analyticsEvents.helpButtonClicked('start_page');
  };

  const renderAnimatedTitle = () => {
    const words = ["LETTER", "HEIST"];
    
    return words.map((word, wordIndex) => {
      const wordLetters = word.split('').map((letter, letterIndex) => {
        // Calculate overall letter index for animation cycling
        const overallIndex = wordIndex === 0 ? letterIndex : 6 + letterIndex; // "LETTER" = 6 letters
        const nonSpaceIndex = overallIndex;
      
        // Cycle through different animations, but handle swaps in pairs
        const animationCycle = Math.floor(nonSpaceIndex / 2) % 3; // Groups of 2 letters
        let animationType;
        
        if (animationCycle === 1) { // Swap animation for pairs
          animationType = nonSpaceIndex % 2 === 0 ? 'letterSwapRight' : 'letterSwapLeft';
        } else if (animationCycle === 2) {
          animationType = 'letterGlitch';
        } else {
          animationType = 'letterFloat';
        }
        
        // Synchronized timing for swap pairs, varied for others
        let baseDuration, delay;
        if (animationType.includes('Swap')) {
          baseDuration = 4; // Same duration for swap pairs
          delay = Math.floor(nonSpaceIndex / 2) * 0.8; // Pairs start together
        } else {
          baseDuration = 3 + (nonSpaceIndex % 3); // 3-5 seconds
          delay = (nonSpaceIndex * 0.3) % baseDuration; // Staggered delays
        }
        
        return (
          <AnimatedLetter
            key={`${wordIndex}-${letterIndex}`}
            $animationName={animationType}
            $duration={baseDuration}
            $delay={delay}
            data-animated-letter
          >
            {letter}
          </AnimatedLetter>
        );
      });
      
      return (
        <div key={wordIndex} style={{ display: 'flex', justifyContent: 'center', gap: '0.1em' }}>
          {wordLetters}
        </div>
      );
    });
  };

    return (
    <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      <SharedGlobalStyle theme={theme === 'light' ? lightTheme : darkTheme} />
      <SharedBackgroundContainer theme={theme === 'light' ? lightTheme : darkTheme}>
        <PageContainer>
          <TopBar>
            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
              <ThemeToggleIcon theme={theme} toggleTheme={toggleTheme} />
            </div>
            <div style={{ width: 48 }} />
            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
              <MuteToggle />
            </div>
          </TopBar>
          <CenteredContent>
            <Title theme={theme}>{renderAnimatedTitle()}</Title>
            {/* <Subtitle theme={theme}>Find words before they disappear!</Subtitle> */}
            <ButtonContainer>
              <RetroLinkButton to="/game">Single Player</RetroLinkButton>
              <RetroLinkButton to="/multiplayer" onClick={playLobbyButtonClickSound}>Multiplayer</RetroLinkButton>
            </ButtonContainer>
            <TutorialButton onClick={handleShowTutorial} theme={theme === 'light' ? lightTheme : darkTheme}>
              HOW TO PLAY
            </TutorialButton>
          </CenteredContent>
        <SEOSection $themeMode={theme}>
          <SEOHeading>Multiplayer Wordle Strategy Game - Compete in Word Battles</SEOHeading>
          <SEOParagraph>
            Letter Heist is a real-time <KeywordHighlight>multiplayer wordle strategy game</KeywordHighlight> for 2–5 players. Everyone plays on the same hidden-letter grid. Tap tiles to reveal letters and race to find five different five-letter words before anyone else. You cannot see opponents' letters, but colored markers show which tiles they touched. Words can go across, down, or diagonal.
          </SEOParagraph>
          <SEOParagraph>
            Finish a valid word to score and get a bonus turn. Swap any two revealed tiles to rearrange letters. Letter Heist runs in your browser—no downloads required. Start a private room with friends or practice alone. Check the HOW TO PLAY section above for a detailed tutorial.
          </SEOParagraph>
        </SEOSection>
          <Footer $themeMode={theme}>
            © {new Date().getFullYear()} Letter Heist •
            <FooterLink as={Link} to="/faq">
              FAQ
            </FooterLink>
            •
            <FooterLink href="https://www.linkedin.com/in/deeptinaidu/" target="_blank" rel="noreferrer">
              LinkedIn
            </FooterLink>
            |
            <FooterLink href="mailto:deepti.naidu@gmail.com">
              Gmail
            </FooterLink>
          </Footer>
        </PageContainer>
        
        <Tutorial 
          isVisible={showTutorial} 
          onComplete={handleTutorialComplete} 
          theme={theme === 'light' ? lightTheme : darkTheme}
        />
      </SharedBackgroundContainer>
    </ThemeProvider>
  );
};

export default StartPage; 